<template>
  <form class="flex flex-col gap-3" @submit.prevent="handleSubmit">
    <textarea
      ref="contentsTextarea"
      v-model="contents"
      class="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
      :placeholder="placeholder"
      :disabled="readonly || loading"
      @paste="handleTextareaPaste"
    />

    <p v-if="uploadErrorMessage" class="text-xs text-red-600">
      {{ uploadErrorMessage }}
    </p>

    <div v-if="uploadedFiles.length > 0" class="flex flex-col gap-2">
      <p class="text-xs font-medium text-slate-700">
        {{ attachmentsLabel }}
      </p>
      <div class="flex flex-wrap gap-2">
        <UploadedFile v-for="uploadedFile in uploadedFiles" :key="uploadedFile.id" :file="uploadedFile" :auto-delete="false" :deletable="true" @delete="removeUploadedFile(uploadedFile.id)" />
      </div>
    </div>

    <div class="flex items-center justify-end gap-2">
      <UploadButton
        v-if="allowAttachments"
        ref="uploadButtonRef"
        :multiple="true"
        :disabled="readonly || loading"
        :label="attachButtonLabel"
        @upload-start="handleUploadStart"
        @upload-finish="handleUploadFinish"
        @uploaded="handleUploaded"
        @error="handleUploadError"
      />
      <button
        class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
        type="submit"
        :disabled="readonly || loading || isUploadingFiles || contents.trim().length === 0"
      >
        {{ loading ? submittingLabel : submitLabel }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'
import UploadButton from '../../../../../uploadable/resources/js/components/UploadButton.vue'
import UploadedFile from '../../../../../uploadable/resources/js/components/UploadedFile.vue'
import { extractPastedImageBlobs } from './noteAddFormPaste.js'

const props = defineProps({
  allowAttachments: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Add a note',
  },
  submitLabel: {
    type: String,
    default: 'Add note',
  },
  submittingLabel: {
    type: String,
    default: 'Saving...',
  },
  attachButtonLabel: {
    type: String,
    default: 'Attach files',
  },
  attachmentsLabel: {
    type: String,
    default: 'Attachments',
  },
  removeAttachmentLabel: {
    type: String,
    default: 'Remove',
  },
  uploadErrorLabel: {
    type: String,
    default: 'Unable to upload selected files.',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['submit'])

const contents = ref('')
const contentsTextarea = ref(null)
const uploadButtonRef = ref(null)
const uploadedFiles = ref([])
const uploadErrorMessage = ref('')
const isUploadingFiles = ref(false)

const normalizedUploadableIds = computed(() => {
  return uploadedFiles.value.map((uploadedFile) => Number(uploadedFile?.id ?? 0)).filter((uploadId) => Number.isInteger(uploadId) && uploadId > 0)
})

const resetForm = () => {
  contents.value = ''
  uploadedFiles.value = []
  uploadErrorMessage.value = ''
}

const handleSubmit = async () => {
  if (props.readonly) {
    return
  }

  const trimmedContents = contents.value.trim()
  if (trimmedContents.length === 0) {
    return
  }

  await emit('submit', {
    contents: trimmedContents,
    ...(normalizedUploadableIds.value.length > 0 ? { uploadable_ids: normalizedUploadableIds.value } : {}),
  })

  resetForm()
}

const focusInput = () => {
  contentsTextarea.value?.focus()
}

const handleUploadStart = () => {
  isUploadingFiles.value = true
  uploadErrorMessage.value = ''
}

const handleUploadFinish = () => {
  isUploadingFiles.value = false
}

const handleUploaded = (payload) => {
  const uploadedItems = Array.isArray(payload) ? payload : [payload]

  for (const uploadedItem of uploadedItems) {
    const uploadId = Number(uploadedItem?.id ?? 0)
    if (!Number.isInteger(uploadId) || uploadId <= 0) {
      continue
    }

    const alreadyAdded = uploadedFiles.value.some((existingUpload) => Number(existingUpload?.id ?? 0) === uploadId)
    if (alreadyAdded) {
      continue
    }

    uploadedFiles.value.push(uploadedItem)
  }

  uploadErrorMessage.value = ''
}

const handleUploadError = (error) => {
  uploadErrorMessage.value = error?.response?.data?.message ?? error?.message ?? props.uploadErrorLabel
}

const removeUploadedFile = (uploadId) => {
  uploadedFiles.value = uploadedFiles.value.filter((uploadedFile) => Number(uploadedFile?.id ?? 0) !== Number(uploadId))
}

const handleTextareaPaste = async (event) => {
  if (props.readonly) {
    return
  }

  if (!props.allowAttachments) {
    return
  }

  const pastedImageBlobs = extractPastedImageBlobs(event?.clipboardData)

  if (pastedImageBlobs.length === 0) {
    return
  }

  event.preventDefault()

  try {
    await uploadButtonRef.value?.uploadFiles?.(pastedImageBlobs)
  } catch {
    return
  }
}

defineExpose({
  focusInput,
})
</script>
